import React, { useMemo } from 'react';
import {
  ConvertAllocationsProps,
  DeleteAllocationsProps,
  ResourceType,
} from 'api/resource-management/types';
import { ProjectAllocation } from 'components/organisms/resource-management/scheduler-config/columns/cells/allocation';
import { AssignUserOrTeam } from 'components/organisms/resource-management/scheduler-config/columns/cells/assign-user-or-team';
import { ProjectCellForProjectsView } from 'components/organisms/resource-management/scheduler-config/columns/cells/project-for-projects-view';
import { RoleForProjectView } from 'components/organisms/resource-management/scheduler-config/columns/cells/role-for-projects-view';
import { TeamCellForProjectsView } from 'components/organisms/resource-management/scheduler-config/columns/cells/team-for-projects-view';
import { UserCellForProjectsView } from 'components/organisms/resource-management/scheduler-config/columns/cells/user-for-projects-view';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { PrereleaseFeature, useFeature } from 'hooks/use-feature';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { SchedulerResourceType } from '../../resource-types';
import { SchedulerReference, TeamLite } from '../../types';
import { UserRemovedFromProjectParams } from '../listeners';
import { ColumnConfig } from './types';

interface Props {
  schedulerRef: SchedulerReference;
  softAllocationsEnabled: boolean;
  onAddUsers: (projectId: number) => void;
  onAddTeams: (projectId: number) => void;
  onFindMember: (props: TeamLite) => void;
  onEditTeam: (teamId: number) => void;
  onShiftProjectStartDate: (projectId: number) => void;
  deleteAllocationHandler: (params: DeleteAllocationsProps) => void;
  convertAllocationHandler: (params: ConvertAllocationsProps) => void;
}

export const useColumnsConfigForGroupByProjects = ({
  schedulerRef,
  softAllocationsEnabled,
  onAddUsers,
  onAddTeams,
  onFindMember,
  onEditTeam,
  onShiftProjectStartDate,
  deleteAllocationHandler,
  convertAllocationHandler,
}: Props): ColumnConfig[] => {
  const projectColumnTitle = useTranslation('projects.title');
  const allocationColumnTitle = useTranslation(
    'resource_management.columns.allocation',
  );
  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const showShiftStartDate = useFeature(
    PrereleaseFeature.ShiftStartDateInResourceManagement,
  );

  // TODO: To try and use the more actions hook here

  return useMemo(
    () => [
      {
        text: projectColumnTitle,
        width: 360,
        type: 'tree',
        renderer: (arg) => {
          try {
            const { record, size, cellElement } = arg;
            const { type, autoAllocation } = record;

            record.cls = `${type}${
              autoAllocation === true ? ' auto-allocate' : ''
            }`;

            switch (type as SchedulerResourceType) {
              case SchedulerResourceType.Role:
                size.height = 32;
                cellElement.classList.add('full-width');

                return (
                  <RoleForProjectView
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                    roleName={record.roleName}
                  />
                );
              case SchedulerResourceType.User:
                if (record.referenceTeam == null) {
                  size.height = 48;
                }

                return (
                  <UserCellForProjectsView
                    firstName={record.firstName}
                    lastName={record.lastName}
                    photoUrl={record.photoUrl}
                    company={record.company}
                    userType={record.userType}
                    role={record.role}
                    projectId={record.projectId}
                    userId={record.userId}
                    isProjectOwner={record.isProjectOwner}
                    roleId={record.roleId}
                    referenceTeam={record.referenceTeam}
                    softAllocationsEnabled={softAllocationsEnabled}
                    removeUserFromProjectInUI={(
                      params: UserRemovedFromProjectParams,
                    ): void =>
                      schedulerRef.current.instance.trigger(
                        'onUserRemovedFromProject',
                        params,
                      )
                    }
                    onDeleteAllocations={() => {
                      deleteAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.User,
                          userId: record.userId,
                          ...(record.referenceTeam
                            ? {
                                roleId: record.roleId,
                                referenceTeamId: record.referenceTeam.teamId,
                              }
                            : {}),
                        },
                      });
                    }}
                    onConvertAllocations={() => {
                      convertAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.User,
                          userId: record.userId,
                          ...(record.referenceTeam
                            ? {
                                roleId: record.roleId,
                                referenceTeamId: record.referenceTeam.teamId,
                              }
                            : {}),
                        },
                      });
                    }}
                    autoAllocation={autoAllocation}
                  />
                );
              case SchedulerResourceType.Team:
                size.height = 48;

                return (
                  <TeamCellForProjectsView
                    teamName={record.teamName}
                    roleId={record.roleId}
                    projectId={record.projectId}
                    teamId={record.teamId}
                    userList={record.userList}
                    companyList={record.companyList}
                    softAllocationsEnabled={softAllocationsEnabled}
                    onFindMember={() =>
                      onFindMember({
                        projectId: record.projectId,
                        teamId: record.teamId,
                        teamName: record.teamName,
                        roleId: record.roleId,
                      })
                    }
                    onEditTeam={() => onEditTeam(record.teamId)}
                    onDeleteAllocations={() => {
                      deleteAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.Role,
                          roleId: record.roleId,
                          teamId: record.teamId,
                        },
                      });
                    }}
                    onConvertAllocations={() => {
                      convertAllocationHandler({
                        projectId: record.projectId,
                        resourceIdentifier: {
                          type: ResourceType.Role,
                          roleId: record.roleId,
                          teamId: record.teamId,
                        },
                      });
                    }}
                    onTeamRemoval={(projectId, teamId) => {
                      schedulerRef.current.instance.trigger(
                        'onTeamRemovedFromProject',
                        {
                          projectId,
                          teamId,
                        },
                      );
                    }}
                    autoAllocation={autoAllocation}
                  />
                );
              case SchedulerResourceType.Project:
                return (
                  <ProjectCellForProjectsView
                    projectId={record.projectId}
                    projectName={record.projectName}
                    customer={record.customer}
                    status={record.status}
                    startDate={record.startDate}
                    showShiftStartDate={showShiftStartDate}
                    softAllocationsEnabled={softAllocationsEnabled}
                    onConvertAllocations={() => {
                      convertAllocationHandler({
                        projectId: record.projectId,
                      });
                    }}
                    onShiftStartDate={() => {
                      onShiftProjectStartDate(record.projectId);
                    }}
                    onWrapperClick={() => {
                      schedulerRef.current.instance.toggleCollapse(record.id);
                    }}
                    goToStartOfProject={() => {
                      schedulerRef.current.instance.trigger(
                        'onToStartOfProject',
                        {
                          projectId: record.projectId,
                        },
                      );
                    }}
                    autoAllocation={autoAllocation}
                    enableAutoAllocate={() =>
                      schedulerRef.current.instance.trigger(
                        'onAutoAllocationStateChange',
                        {
                          projectId: record.projectId,
                          autoAllocation: true,
                        },
                      )
                    }
                    disableAutoAllocate={() =>
                      schedulerRef.current.instance.trigger(
                        'onAutoAllocationStateChange',
                        {
                          projectId: record.projectId,
                          autoAllocation: false,
                        },
                      )
                    }
                  />
                );
              case SchedulerResourceType.AssignUserOrPlaceholder:
                size.height = 48;
                cellElement.classList.add('full-width');

                return (
                  <AssignUserOrTeam
                    addUsers={() => onAddUsers(record.projectId)}
                    addTeams={() => onAddTeams(record.projectId)}
                  />
                );
              default:
                return '';
            }
          } catch (e) {
            logger.log('Error in rendering columns for projects', e);

            return <></>; // ? Can we handle it better ?
          }
        },
      },
      {
        text: allocationColumnTitle,
        width: 104,
        id: 'allocatedHours',
        renderer: (arg) => {
          try {
            const { record } = arg;
            const { type, autoAllocation } = record;

            switch (type as SchedulerResourceType) {
              case SchedulerResourceType.User:
              case SchedulerResourceType.Team:
              case SchedulerResourceType.Project:
                return (
                  <ProjectAllocation
                    budgetedHours={record.budgetedHours}
                    totalAllocation={record.totalAllocation}
                    isTimesheetFeatureEnabled={isTimesheetFeatureEnabled}
                    showDetailedView={type === SchedulerResourceType.Project}
                    autoAllocate={
                      type === SchedulerResourceType.Project
                        ? autoAllocation
                        : false
                    }
                    dataLoading={record.dataLoading}
                  />
                );
              case SchedulerResourceType.Role:
              default:
                return '';
            }
          } catch (e) {
            logger.log('Error in rendering project allocation column', e);

            return <></>; // ? Can we handle it better ?
          }
        },
      },
    ],
    [
      projectColumnTitle,
      allocationColumnTitle,
      softAllocationsEnabled,
      showShiftStartDate,
      schedulerRef,
      deleteAllocationHandler,
      convertAllocationHandler,
      onFindMember,
      onEditTeam,
      onShiftProjectStartDate,
      onAddUsers,
      onAddTeams,
      isTimesheetFeatureEnabled,
    ],
  );
};
